import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { DataTable, type DataTableColumn } from '../../components/ui/DataTable';
import { formatStatusBadge } from '../../components/ui/table-helpers';
import ReservationDetailsDialog from '../../components/dialogs/ReservationDetailsDialog';
import { 
  Calendar, 
  Clock, 
  Users, 
  Phone, 
  Search,
  Download,
  Eye,
  RotateCcw,
  XCircle,
  AlertCircle,
  MapPin,
  ChevronLeft,
  ChevronRight,

  Table,
  Mail,
  Timer,
  TimerReset
} from 'lucide-react';
import { reservationApi } from '../../services/reservationApi';
import webSocketService, { type PendingBookingsResponse } from '../../services/webSocketService';
import toast from 'react-hot-toast';
import type { ReserveTable, Reservation, ReserveTableDTO } from '../../types/reservations';
import { convertReserveTableToReservation } from '../../types/reservations';

// Extended interface for historical reservation display
interface HistoricalReservation extends Reservation, Record<string, unknown> {
  totalAmount?: number;
  notes?: string;
  updatedAt: string;
}

export default function ReservationHistory() {
  const [reservations, setReservations] = useState<HistoricalReservation[]>([]);
  const [filteredReservations, setFilteredReservations] = useState<HistoricalReservation[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('month'); // today, week, month, year (backend doesn't support 'all')
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [selectedReservation, setSelectedReservation] = useState<HistoricalReservation | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => Promise<void> | void;
    type: 'destructive' | 'warning' | 'info';
  } | null>(null);
  const pageSize = 10;
  
  // Database-driven status counts for accuracy
  const [statusCounts, setStatusCounts] = useState({
    completed: 0,
    cancelled: 0,
    rejected: 0,
    accepted: 0,
    pending: 0,
    incompleted: 0,
    total: 0
  });
  
  // Loading state for better UX
  const [countsLoading] = useState(false);

  // Convert backend ReserveTable to HistoricalReservation
  const convertToHistoricalReservation = (reservation: ReserveTable): HistoricalReservation => {
    const baseReservation = convertReserveTableToReservation(reservation);
    return {
      ...baseReservation,
      updatedAt: new Date().toISOString(), // Backend doesn't provide updatedAt
      totalAmount: undefined, // Backend doesn't provide totalAmount in ReserveTable
      notes: undefined, // Backend doesn't provide notes field
    };
  };

  // Convert ReserveTableDTO to HistoricalReservation
  const convertDTOToHistoricalReservation = (dto: ReserveTableDTO): HistoricalReservation => {
    // Map backend BookingStatus to frontend status
    const statusMap: Record<string, Reservation['status']> = {
      'Accepted': 'accepted',
      'Rejected': 'rejected',
      'Pending': 'pending',
      'Completed': 'completed',
      'Canceled': 'cancelled',
      'Incompleted': 'incompleted'
    };

    return {
      id: dto.bookedId, // Use bookedId as id since ReserveTableDTO doesn't have numeric id
      bookedId: dto.bookedId,
      customerName: dto.username,
      customerEmail: dto.email,
      customerPhone: dto.phoneNo,
      date: dto.date,
      time: dto.time,
      guestNo: dto.guestNo,
      specialRequests: dto.message,
      status: statusMap[dto.status] || 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tableId: undefined,
      tableNo: 0, // ReserveTableDTO doesn't provide table number
      totalAmount: undefined,
      notes: undefined,
    };
  };

  // Combined function to fetch both reservations and counts in one API call
  const loadReservationsAndCounts = useCallback(async (showRefresh = false) => {
    try {
      setIsLoading(!showRefresh);
      setError(null);
      
      if (showRefresh) {
        toast.loading('Refreshing reservations...', { id: 'load-reservations' });
      }
      
      if (searchTerm) {
        // Use search API when searching
        const searchResults = await reservationApi.searchReservationsWithValue(searchTerm);
        const convertedReservations = searchResults.map(convertDTOToHistoricalReservation);
        setReservations(convertedReservations);
        setTotalCount(convertedReservations.length);
        setTotalPages(1);
      } else if (periodFilter && periodFilter !== 'month') {
        // Use period search when period filter is specifically set (but not default month)
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
        const periodResults = await reservationApi.searchReservationsByPeriodWithParams(today, periodFilter);
        const convertedReservations = periodResults.map(convertDTOToHistoricalReservation);
        setReservations(convertedReservations);
        setTotalCount(convertedReservations.length);
        setTotalPages(1);
      } else {
        // Use standardized data fetching for consistency with ReservationsList component
        const standardizedData = await reservationApi.getStandardizedReservations();
        
        // Update status counts from fresh database data
        const counts = {
          completed: standardizedData.counts.completed || 0,
          cancelled: standardizedData.counts.cancelled || 0,
          rejected: standardizedData.counts.rejected || 0,
          accepted: standardizedData.counts.accepted || 0,
          pending: standardizedData.counts.pending || 0,
          incompleted: 0, // Not available in API
          total: standardizedData.counts.total || 0
        };
        setStatusCounts(counts);
        
        // Use all reservations for history view - NO CLIENT-SIDE PAGINATION
        const convertedReservations = standardizedData.all.map(convertToHistoricalReservation);
        
        // Sort by date and time (newest first)
        convertedReservations.sort((a, b) => {
          const dateCompare = new Date(b.date).getTime() - new Date(a.date).getTime();
          if (dateCompare !== 0) return dateCompare;
          return b.time.localeCompare(a.time);
        });

        
        // Set all reservations without pagination for now - better UX
        setReservations(convertedReservations);
        setTotalCount(standardizedData.counts.total);
        setTotalPages(1); // Show all on one page for better UX
        
        if (showRefresh) {
          toast.success(`Loaded ${convertedReservations.length} reservations`, { id: 'load-reservations' });
        }
      }
    } catch (err) {
      setError('Failed to load reservations. Please try again.');
      setReservations([]);
      
      if (showRefresh) {
        toast.error('Failed to refresh reservations', { id: 'load-reservations' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, periodFilter]);

  // Legacy method for backward compatibility
  const loadReservations = useCallback(async (showRefresh = false) => {
    await loadReservationsAndCounts(showRefresh);
  }, [loadReservationsAndCounts]);



  // Apply filters whenever reservations or filter criteria change
  useEffect(() => {
    let filtered = reservations;
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(reservation =>
        reservation.customerName.toLowerCase().includes(searchLower) ||
        reservation.customerPhone.includes(searchTerm) ||
        reservation.bookedId.toLowerCase().includes(searchLower) ||
        reservation.customerEmail.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(reservation => reservation.status === statusFilter);
    }
    
    // Apply date filter
    if (dateFilter) {
      filtered = filtered.filter(reservation => reservation.date === dateFilter);
    }
    
    setFilteredReservations(filtered);
  }, [reservations, searchTerm, statusFilter, dateFilter]);

  // Initial load and reload when filters change
  useEffect(() => {
    loadReservations();
  }, [loadReservations, currentPage, periodFilter]);

  // WebSocket connection and component registration
  useEffect(() => {
    const componentId = 'ReservationHistory';
    let statusTimeout: NodeJS.Timeout;
    
    // Register this component with the WebSocket service
    webSocketService.registerComponent(componentId);
    
    // Get initial connection status immediately
    const initialStatus = webSocketService.getConnectionStatus() === 'connected';
    
    // Listen to connection status with debouncing
    const connectionSub = webSocketService.connectionStatus().subscribe({
      next: (connected: boolean) => {
        // Debounce to prevent flicker during tab switches
        if (statusTimeout) clearTimeout(statusTimeout);
        statusTimeout = setTimeout(() => {
         
        }, 100); // 100ms debounce
      },
      error: (error: unknown) => {
        
      }
    });

    // Listen to real-time updates with debouncing to prevent excessive API calls
    let updateTimeout: NodeJS.Timeout | null = null;
    const updatesSub = webSocketService.pendingBookings().subscribe({
      next: (pendingData: PendingBookingsResponse) => {
        
        // Debounce updates to prevent multiple rapid API calls
        if (updateTimeout) clearTimeout(updateTimeout);
        updateTimeout = setTimeout(() => {
          // Use the combined function to reduce API calls
          loadReservationsAndCounts(true);
        }, 2000); // 2 second debounce for history (longer since less critical)
      },
      error: (error: unknown) => {
      }
    });

    // Cleanup function
    return () => {
      if (statusTimeout) clearTimeout(statusTimeout);
      if (updateTimeout) clearTimeout(updateTimeout);
      webSocketService.unregisterComponent(componentId);
      connectionSub.unsubscribe();
      updatesSub.unsubscribe();
    };
  }, [loadReservationsAndCounts]);


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Show confirmation dialog
  const showConfirmation = (title: string, message: string, action: () => Promise<void> | void, type: 'destructive' | 'warning' | 'info' = 'warning') => {
    setConfirmationDialog({ open: true, title, message, action, type });
  };

  // Handle confirmation dialog response
  const handleConfirmation = async (confirmed: boolean) => {
    if (confirmed && confirmationDialog?.action) {
      try {
        await confirmationDialog.action();
      } catch (error) {
      }
    }
    setConfirmationDialog(null);
  };

  // Handle cancel reservation with proper database sync
  const handleCancelReservation = async (reservation: HistoricalReservation) => {
    const cancelAction = async () => {
      try {
        toast.loading('Cancelling reservation...', { id: 'cancel-reservation' });
        
        // Extract ALL table IDs for merged groups
        let tableIds: number[] = [1]; // Default fallback

        // Try multiple ways to get table IDs (priority order)
        // 1. Try restaurantTableId array first (modern structure) - extract ALL IDs
        if (reservation.restaurantTableId && Array.isArray(reservation.restaurantTableId) && reservation.restaurantTableId.length > 0) {
          const extractedIds = reservation.restaurantTableId.map(table => 
            typeof table === 'object' ? table.id : table
          ).filter(id => id && id > 0);
          
          if (extractedIds.length > 0) {
            tableIds = extractedIds;
          }
        } 
        // 2. Try restaurantTable object (legacy structure)
        else if (reservation.restaurantTable && typeof reservation.restaurantTable === 'object') {
          const tableObj = reservation.restaurantTable as { id?: number };
          if (tableObj.id && tableObj.id > 0) {
            tableIds = [tableObj.id];
          }
        } 
        // 3. Try tableNo field
        else if (reservation.tableNo && reservation.tableNo > 0) {
          tableIds = [reservation.tableNo];
        } 
        // 4. Try tableId string field
        else if (reservation.tableId && typeof reservation.tableId === 'string') {
          const parsed = parseInt(reservation.tableId);
          if (!isNaN(parsed) && parsed > 0) {
            tableIds = [parsed];
          }
        }
        
        // If we still have 0 or negative, use default
        if (tableIds.length === 0 || tableIds[0] <= 0) {
          tableIds = [1];
        }
        
        
        // Call API to cancel the reservation with all table IDs
        const response = await reservationApi.cancelBooking(
          parseInt(reservation.id), // Use reservation.id instead of bookedId
          tableIds,
          'Cancelled by admin from history view',
          undefined, // customerDetails
          reservation.mergeGroupName,
          reservation.mergeGroupColor
        );
        
        toast.success(`Reservation for ${reservation.customerName} has been cancelled`, { id: 'cancel-reservation' });
        
        // Force refresh with database sync - don't rely on WebSocket
        await loadReservationsAndCounts(true); // Single call to refresh both reservations and counts
        
      } catch (error) {
        toast.error(`Failed to cancel reservation: ${error instanceof Error ? error.message : 'Unknown error'}`, { id: 'cancel-reservation' });
      }
    };

    showConfirmation(
      'Cancel Reservation',
      `Are you sure you want to cancel the reservation for "${reservation.customerName}"? This action cannot be undone.`,
      cancelAction,
      'destructive'
    );
  };

  // Enhanced export functionality with confirmation
  const exportToCSV = () => {
    const exportAction = () => {
      try {
        toast.loading('Preparing CSV export...', { id: 'csv-export' });
        
        const headers = ['ID', 'Customer Name', 'Phone', 'Email', 'Date', 'Time', 'Guest Count', 'Status', 'Table', 'Special Requests'];
        const csvContent = [
          headers.join(','),
          ...filteredReservations.map(reservation => [
            reservation.id,
            `"${reservation.customerName.replace(/"/g, '""')}"`,
            reservation.customerPhone,
            reservation.customerEmail,
            reservation.date,
            reservation.time,
            reservation.guestNo,
            reservation.status,
            reservation.tableId || 'N/A',
            `"${(reservation.specialRequests || 'None').replace(/"/g, '""')}"`
          ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', `reservation_history_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Successfully exported ${filteredReservations.length} reservations to CSV`, { id: 'csv-export' });
      } catch (error) {
        toast.error('Failed to export reservations. Please try again.', { id: 'csv-export' });
      }
    };

    if (filteredReservations.length === 0) {
      toast.error('No reservations to export');
      return;
    }

    showConfirmation(
      'Export Reservations',
      `Export ${filteredReservations.length} reservations to CSV file?`,
      exportAction,
      'info'
    );
  };

  // View reservation details
  const handleViewReservation = (reservation: HistoricalReservation) => {
    setSelectedReservation(reservation);
    setDetailsDialogOpen(true);
  };

  // Handle reservation actions from details dialog
  const handleReservationAction = async (action: string) => {
    if (!selectedReservation) return;
    
    if (action === 'cancel') {
      await handleCancelReservation(selectedReservation);
      setDetailsDialogOpen(false);
      setSelectedReservation(null);
    }
  };

  // Define table columns for DataTable
  const tableColumns: DataTableColumn<HistoricalReservation>[] = [
    {
      key: 'customerName',
      header: 'Customer',
      render: (_, row) => (
        <div>
          <div className="font-medium">{row.customerName}</div>
          <div className="text-sm text-muted-foreground">#{row.id}</div>
        </div>
      )
    },
    {
      key: 'date',
      header: 'Date & Time',
      render: (_, row) => (
        <div>
          <div className="font-medium">{formatDate(row.date)}</div>
          <div className="text-sm text-muted-foreground">{row.time}</div>
        </div>
      )
    },
    {
      key: 'guestNo',
      header: 'Guests',
      render: (value) => (
        <div className="flex items-center">
          <Users className="h-4 w-4 mr-1 text-muted-foreground" />
          {String(value)}
        </div>
      )
    },
    {
      key: 'customerPhone',
      header: 'Contact',
      render: (_, row) => (
        <div>
          <div className="flex items-center text-sm">
            <Phone className="h-3 w-3 mr-1 text-muted-foreground" />
            {row.customerPhone}
          </div>
        </div>
      )
    },
    {
      key: 'customerEmail',
      header: 'Email',
      render: (_, row) => (
        <div>
          <div className="flex items-center text-sm">
            <Mail className="h-3 w-3 mr-1 text-muted-foreground" />
            {row.customerEmail}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => formatStatusBadge(String(value))
    },
    {
      key: 'restaurantTableId',
      header: 'Table',
      render: (value) => {
        if (Array.isArray(value) && value.length > 0) {
          return value.map((t: { name: string }) => t.name).join(', ');
        }
        return 'N/A';
      }
    }
  ];

  const tableActions = [
    {
      key: 'view',
      label: 'View Details',
      icon: <Eye className="h-4 w-4" />,
      variant: 'outline' as const
    },
    {
      key: 'cancel',
      label: 'Cancel Reservation',
      icon: <XCircle className="h-4 w-4 text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 " />,
      variant: 'destructive' as const,
      condition: (row: HistoricalReservation) => row.status === 'accepted'
    }
  ];


  const handleTableAction = (action: string, row: HistoricalReservation) => {
    if (action === 'view') {
      handleViewReservation(row);
    } else if (action === 'cancel') {
      handleCancelReservation(row);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-muted-foreground">
            View and manage reservation records
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground " />
            <Input
              placeholder="Search by name, phone, email, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-background !rounded"
            />
          </div>
        </div>
        
        <div className="flex gap-2">
          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            className="px-3 py-2 rounded-md bg-background"
          >
            <option value="month">This Month</option>
            <option value="week">This Week</option>
            <option value="today">Today</option>
            <option value="year">This Year</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2  border bg-background !rounded"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="rejected">Rejected</option>
            <option value="accepted">Accepted</option>
            <option value="pending">Pending</option>
            <option value="incompleted">Incompleted</option>
          </select>

          <Input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-40 bg-background !rounded"
          />

          <Button 
            variant="outline" 
            onClick={() => {
              const hasActiveFilters = searchTerm || statusFilter !== 'all' || dateFilter || periodFilter !== 'month';
              
              if (hasActiveFilters) {
                const clearAction = () => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setDateFilter('');
                  setPeriodFilter('month');
                  setCurrentPage(0);
                  loadReservations(true);
                  toast.success('All filters cleared and data refreshed');
                };
                
                showConfirmation(
                  'Clear All Filters',
                  'This will reset all active filters and refresh the data. Continue?',
                  clearAction,
                  'info'
                );
              } else {
                loadReservations(true);
                toast.success('Data refreshed');
              }
            }}
            title="Clear filters and refresh"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>

          <Button variant="outline" onClick={exportToCSV} title="Export to CSV">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          
          <div className="flex border rounded-md overflow-hidden">
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => {
                if (viewMode !== 'cards') {
                  setViewMode('cards');
                  toast.success('Switched to cards view');
                }
              }}
              className="rounded-none border-none"
            >
              Cards View
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => {
                if (viewMode !== 'table') {
                  setViewMode('table');
                  toast.success('Switched to table view');
                }
              }}
              className="rounded-none border-none"
            >
              <Table className="h-4 w-4 mr-1" />
              Table View
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards - Database Driven */}
      {/* Display loading spinner for counts if still loading */}
      {(() => {
        const displayCount = (count: number) => countsLoading ? '...' : count.toString();
        return (
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {displayCount(statusCounts.completed)}
            </div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </CardContent>
        </Card> */} 
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow hover:border-green-400"
          onClick={() => setStatusFilter(statusFilter === 'accepted' ? 'all' : 'accepted')}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {displayCount(statusCounts.accepted)}
            </div>
            <div className="text-sm text-muted-foreground">Accepted</div>
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow hover:border-red-400"
          onClick={() => setStatusFilter(statusFilter === 'cancelled' ? 'all' : 'cancelled')}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600">
              {displayCount(statusCounts.cancelled)}
            </div>
            <div className="text-sm text-muted-foreground">Cancelled</div>
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow hover:border-red-300"
          onClick={() => setStatusFilter(statusFilter === 'rejected' ? 'all' : 'rejected')}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-500">
              {displayCount(statusCounts.rejected)}
            </div>
            <div className="text-sm text-muted-foreground">Rejected</div>
          </CardContent>
        </Card>
       
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow hover:border-purple-400"
          onClick={() => setStatusFilter('all')}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600">
              {displayCount(statusCounts.total)}
            </div>
            <div className="text-sm text-muted-foreground">Total</div>
          </CardContent>
        </Card>
      </div>
        );
      })()}

      {/* Error Display */}
      {error && (
        <Card>
          <CardContent className="p-4">
            <div className="text-red-600 text-center">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" />
              <p>{error}</p>
              <Button 
                variant="outline" 
                onClick={() => {
                  toast.loading('Retrying...', { id: 'retry-load' });
                  loadReservations(true);
                }} 
                className="mt-2"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reservations Display */}
      <div className="space-y-4">
        {(() => {
          if (isLoading) {
            return (
              <div className="text-center py-8">
                <LoadingSpinner />
                <p className="text-muted-foreground mt-2">Loading reservations...</p>
              </div>
            );
          }
          
          if (filteredReservations.length === 0 && !error) {
            return (
              <Card>
                <CardContent className="p-8 text-center">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchTerm || statusFilter !== 'all' || dateFilter ? 'No reservations match your filters' : 'No historical reservations found'}
                  </p>
                </CardContent>
              </Card>
            );
          }
          
          if (viewMode === 'table') {
            return (
              <DataTable<HistoricalReservation>
                data={filteredReservations}
                columns={tableColumns}
                title="Reservation History"
                searchable={false} // We handle search separately
                actions={tableActions}
                onActionClick={handleTableAction}
                onRowClick={handleViewReservation}
                pageSize={pageSize}
              />
            );
          }
          
          return (
          filteredReservations.map((reservation, index) => {
            // Create unique key using multiple identifiers to avoid duplication
            const uniqueKey = `hist-${reservation.bookedId}-${reservation.id}-${index}`;
            return (
            <Card key={uniqueKey} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="font-semibold text-lg">{reservation.customerName}</div>
                    <div className="text-sm text-muted-foreground">#{reservation.id}</div>
                    {formatStatusBadge(reservation.status)}
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleViewReservation(reservation)}
                      title="View reservation details"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {/* Add cancel button for accepted reservations */}
                    {reservation.status === 'accepted' && (
                      <Button 
                        variant="red" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                        onClick={() => {     
                          handleCancelReservation(reservation);
                        }}
                        title="Cancel reservation"
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{formatDate(reservation.date)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{reservation.time}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{reservation.guestNo} guests</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{reservation.customerPhone}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{reservation.customerEmail}</span>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {reservation.restaurantTableId && reservation.restaurantTableId.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {reservation.restaurantTableId.map(t => t.name).join(', ')}
                      </span>
                    </div>
                  )}
                  
                  
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Timer className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Created: {formatDate(reservation.createdAt)}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <TimerReset className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Updated: {formatDate(reservation.updatedAt)}</span>
                  </div>
                </div>

                {reservation.specialRequests && (
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-sm font-medium text-blue-800 dark:text-blue-200">Special Requests:</div>
                    <div className="text-sm text-blue-700 dark:text-blue-300">{reservation.specialRequests}</div>
                  </div>
                )}

                {reservation.notes && (
                  <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="text-sm font-medium">Notes:</div>
                    <div className="text-sm text-muted-foreground">{reservation.notes}</div>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })
          );
        })()}
      </div>

      {/* Pagination */}
      {totalCount > 0 && !error && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {filteredReservations.length} of {totalCount} reservations
            {searchTerm && ` (filtered by "${searchTerm}")`}
            {statusFilter !== 'all' && ` (${statusFilter} only)`}
          </div>
          <div className="flex space-x-2 items-center">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={currentPage === 0 || isLoading}
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {currentPage + 1} of {totalPages}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={currentPage >= totalPages - 1 || isLoading}
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Reservation Details Dialog */}
      <ReservationDetailsDialog
        isOpen={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedReservation(null);
        }}
        reservation={selectedReservation}
        onActionClick={handleReservationAction}
      />
      
      {/* Confirmation Dialog */}
      {confirmationDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              {confirmationDialog.type === 'destructive' && (
                <XCircle className="h-6 w-6 text-red-500 mr-3" />
              )}
              {confirmationDialog.type === 'warning' && (
                <AlertCircle className="h-6 w-6 text-yellow-500 mr-3" />
              )}
              {confirmationDialog.type === 'info' && (
                <Calendar className="h-6 w-6 text-blue-500 mr-3" />
              )}
              <h3 className="text-lg font-semibold">{confirmationDialog.title}</h3>
            </div>
            <p className="text-muted-foreground mb-6">{confirmationDialog.message}</p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => handleConfirmation(false)}
              >
                Cancel
              </Button>
              <Button
                variant={confirmationDialog.type === 'destructive' ? 'red' : 'default'}
                onClick={() => handleConfirmation(true)}
              >
                {confirmationDialog.type === 'destructive' ? 'Delete' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
